import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxDataGridComponent, DxValidationGroupComponent } from "devextreme-angular";
import CustomStore from "devextreme/data/custom_store";
import notify from "devextreme/ui/notify";
import { forkJoin, lastValueFrom } from "rxjs";
import { FileTransfersService, IOcbCUFileTransferRecipient, IOcbFileTransferRecipient, OcbFileTransferLogTypes, OcbRemotePlatformLogTypes } from "../../core";

@Component({
    selector: 'app-file-transfer-alerts',
    templateUrl: './file-transfer-alerts.component.html',
    styleUrls: ['./file-transfer-alerts.component.scss']
})
export class FileTransferAlertsComponent implements AfterViewInit {
    @ViewChild('alertsGrid', { static: true }) dataGrid?: DxDataGridComponent;
    @ViewChild('recipientValidation') public recipientValidationGroup: DxValidationGroupComponent;

    protected dataSource: any;
    protected fileTransferLogTypes: string[];
    protected fileTransferLogTypesLabels: any[];
    protected newRecipientData?: IOcbCUFileTransferRecipient;
    protected remotePlatformLogTypes: string[];
    protected remotePlatformLogTypesLabels: any[];
    protected showRecipientCreationPopup: boolean;

    private editingRecipient?: IOcbFileTransferRecipient;
    private fileTransferId: string;
    private loadingMessage: string;
    private recipientCreatedMessage: string;

    constructor(private fileTransfersService: FileTransfersService,
        private route: ActivatedRoute,
        private translateService: TranslateService) {
        this.fileTransferLogTypes = Object.keys(OcbFileTransferLogTypes).filter(key => typeof (OcbFileTransferLogTypes[key]) === 'number');
        this.remotePlatformLogTypes = Object.keys(OcbRemotePlatformLogTypes).filter(key => typeof (OcbRemotePlatformLogTypes[key]) === 'number');
        this.displayFileTransferLogType = this.displayFileTransferLogType.bind(this);
        this.displayRemotePlatformLogType = this.displayRemotePlatformLogType.bind(this);
    }

    ngAfterViewInit() {
        forkJoin({
            ftLogTypes: this.translateService.get('fileTransferLogTypes'),
            rpLogTypes: this.translateService.get('remotePlatformLogTypes'),
            loadingMsg: this.translateService.get('_commons.loading'),
            recipientCreatedMsg: this.translateService.get('alerts.msgAddRecipientComplete')
        }).subscribe({
            next: (data) => {
                this.fileTransferLogTypesLabels = data.ftLogTypes;
                this.remotePlatformLogTypesLabels = data.rpLogTypes;
                this.loadingMessage = data.loadingMsg;
                this.recipientCreatedMessage = data.recipientCreatedMsg;
            },
            error: (err) => this.handleError(err)
        });

        this.route.params.subscribe(_ => {
            if (this.route.snapshot.params.ft === 'new') return;
            this.fileTransferId = this.route.snapshot.params.ft;

            this.dataSource = new CustomStore({
                key: 'id',
                load: async () => {
                    const query$ = this.fileTransfersService.getRecipients(this.fileTransferId);
                    const response = await lastValueFrom(query$);
                    return {
                        data: response,
                        totalCount: response.length
                    };
                },
                update: (key, value) => {
                    const d = {
                        emailAddress: this.editingRecipient.emailAddress,
                        fileTransferLogTypesEnums: this.editingRecipient.fileTransferLogTypesEnums,
                        notificationGroupId: this.editingRecipient.notificationGroupId,
                        remotePlatformLogTypesEnums: this.editingRecipient.remotePlatformLogTypesEnums,
                        userId: this.editingRecipient.user?.id
                    } as IOcbCUFileTransferRecipient;

                    const query$ = this.fileTransfersService.updateRecipient(this.fileTransferId, key, d);
                    return lastValueFrom(query$);
                },
                remove: (key) => {
                    const query$ = this.fileTransfersService.deleteRecipient(this.fileTransferId, key);
                    return lastValueFrom(query$);
                }
            });
        });
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) message = error.error.message;
            else if (error.error.hasOwnProperty('name')) message = error.error.name;
            else message = error.error;
        }
        else message = error;
        notify(message, 'error', 3000);
    }

    private setRecipientFileTransferLogValue(logType: OcbFileTransferLogTypes, value: boolean): void {
        const index = this.editingRecipient.fileTransferLogTypesEnums.indexOf(logType);
        if (value && index === -1) 
            this.editingRecipient.fileTransferLogTypesEnums.push(logType);
        else if (!value && index > -1) 
            this.editingRecipient.fileTransferLogTypesEnums.splice(index, 1);
    }

    private setRecipientRemotePlatformLogValue(logType: OcbRemotePlatformLogTypes, value: boolean): void {
        const index = this.editingRecipient.remotePlatformLogTypesEnums.indexOf(logType);
        if (value && index === -1) 
            this.editingRecipient.remotePlatformLogTypesEnums.push(logType);
        else if (!value && index > -1) 
            this.editingRecipient.remotePlatformLogTypesEnums.splice(index, 1);
    }
    

    public convertFileTransferLogTypeToBoolean(rowData) {
        const column = this as any;
        return rowData.fileTransferLogTypesEnums && rowData.fileTransferLogTypesEnums.indexOf(column.dataField) > -1;
    }

    public convertRemotePlatformLogTypeToBoolean(rowData) {
        const column = this as any;
        return rowData.remotePlatformLogTypesEnums && rowData.remotePlatformLogTypesEnums.indexOf(column.dataField) > -1;
    }

    public createRecipient() {
        if (!this.recipientValidationGroup.instance.validate().isValid) return;

        this.showRecipientCreationPopup = false;
        this.dataGrid.instance.beginCustomLoading(this.loadingMessage);
        this.fileTransfersService.addRecipient(this.fileTransferId, this.newRecipientData).subscribe({
            next: (result) => {
                this.dataGrid.instance.endCustomLoading();
                notify(this.recipientCreatedMessage, 'success', 3000);
                this.dataGrid.instance.refresh();
            },
            error: (e) => {
                this.dataGrid.instance.endCustomLoading();
                this.handleError(e);
            }
        });
    }

    public displayFileTransferLogType(type: string): string { return this.fileTransferLogTypesLabels[type]; }

    public displayRecipientName(recipient: IOcbFileTransferRecipient): string { return recipient.displayName ? recipient.displayName : recipient.emailAddress; }

    public displayRemotePlatformLogType(type: string): string { return this.remotePlatformLogTypesLabels[type]; }

    public isGroup(recipient: IOcbFileTransferRecipient) { return !!recipient.notificationGroupName; }

    public onAddButtonClick(e) {
        this.newRecipientData = {
            emailAddress: null,
            userId: null,
            notificationGroupId: null,
            fileTransferLogTypesEnums: [],
            remotePlatformLogTypesEnums: [],
        };
        this.showRecipientCreationPopup = true;
    }

    public onEditingStart(e) { this.editingRecipient = e.data as IOcbFileTransferRecipient; }

    public onOptionChanged(e) {
        if (e.fullName !== 'editing.changes' || (!e.previousValue && !e.value)) return;
        
        const invertValue = e.value.length === 0;
        const array = (e.value.length > 0 ? e.value : e.previousValue) as any[];
        if (array[0].type !== 'update') return;
        
        const elementData = array[0].data;
        const keys = Object.keys(elementData);
        const values = Object.values(elementData);
        for (let i = 0; i < keys.length; i++) {
            const elementName = keys[i];
            let elementValue = values[i] as boolean;
            if (invertValue) elementValue = !elementValue;

            if (this.fileTransferLogTypes.indexOf(elementName) > -1)
                this.setRecipientFileTransferLogValue(elementName as unknown as OcbFileTransferLogTypes, elementValue);
            else
                this.setRecipientRemotePlatformLogValue(elementName as unknown as OcbRemotePlatformLogTypes, elementValue);
        }
    }

    public onRefreshButtonClick() {
        if (!this.dataGrid) return;
        this.dataGrid.instance.refresh();
    }

    public showTagBoxDropButton(e) { e.component.option('showDropDownButton', true); }
}